/**
 * UpdateAvailableBanner — non-blocking sticky banner that surfaces a
 * stale-SW or stale-bundle update notice. Reload click delegates to
 * the hook's `reload()` callback (whose path is reason-specific); the
 * banner itself never calls `location.reload`, `location.replace`, or
 * any service-worker primitive directly.
 *
 * UX contract:
 *   - z-40, sits BELOW Modal/Dialog overlays (which use z-50).
 *   - role="status" + aria-live="polite" — not a focus-trapping dialog.
 *   - Sticky at bottom-center (matches CookieConsentBanner family).
 *   - Reload button: disabled + aria-busy="true" while the click handler
 *     is in-flight (the underlying SKIP_WAITING/replace navigation may
 *     take a moment).
 *   - Dismiss is session-local only — no localStorage.
 *   - Copy is exactly: "A new version is available. Reload to update."
 */

import { useState } from 'react';
import { usePwaUpdate } from '@/hooks/usePwaUpdate';

export function UpdateAvailableBanner() {
  const { show, reason, reload, dismiss } = usePwaUpdate();
  const [isReloading, setIsReloading] = useState(false);

  if (!show) return null;

  const handleReload = async () => {
    if (isReloading) return;
    setIsReloading(true);
    try {
      await reload();
    } catch {
      // reload() owns its own logging; the banner just resets so the
      // user can try again or dismiss.
      setIsReloading(false);
    }
    // No setIsReloading(false) on success — the page is reloading.
  };

  return (
    <aside
      role="status"
      aria-live="polite"
      data-testid="pwa-update-banner"
      data-pwa-update-reason={reason ?? ''}
      className="fixed inset-x-0 bottom-4 z-40 mx-auto flex max-w-md items-center gap-3 rounded-lg border border-brand-200 bg-white px-4 py-3 shadow-lg sm:bottom-6"
    >
      <span className="flex-1 text-sm text-gray-800">
        A new version is available. Reload to update.
      </span>
      <button
        type="button"
        data-testid="pwa-update-reload"
        onClick={handleReload}
        disabled={isReloading}
        aria-busy={isReloading}
        className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isReloading ? 'Reloading…' : 'Reload'}
      </button>
      <button
        type="button"
        data-testid="pwa-update-dismiss"
        onClick={dismiss}
        disabled={isReloading}
        className="rounded-md px-2 py-1 text-sm text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        Dismiss
      </button>
    </aside>
  );
}
